import { useLocation, Link } from "react-router-dom";
import { useSeo, SITE_URL } from "@/lib/seo";
import { detectLocale, stripLocale, withLocale, SUPPORTED_LOCALES, DEFAULT_LOCALE } from "@/lib/i18n";
import { getHomeDecorCategory, HOME_DECOR_GROUPS, MARKET_NOTES } from "@/data/homeDecor";
import { getHomeDecorTranslation, HOME_DECOR_WAVE1_LOCALES } from "@/data/homeDecorLocales";
import { useStartProject } from "@/lib/startProjectContext";
import NotFound from "@/pages/NotFound";

/**
 * Home-décor category template. Data-driven from /data/homeDecor.js.
 * Route: /categories/home-decor/:slug (all 26 Sprint-28 categories) plus
 * Sprint-29 Wave-1 translated routes /<de|fr|es>/categories/home-decor/:slug.
 * Translations merge on top of the English master (missing fields fall back
 * to English); untranslated locale fallbacks canonicalise to the EN URL.
 * The English render output is byte-identical to the pre-Sprint-29 template.
 */

// English shared strings — exactly the pre-Sprint-29 copy (SEO preservation).
const EN_SHARED = {
  breadcrumbHome: "Home",
  breadcrumbDecor: "Home Décor",
  groups: {},
  subH2: "Product subcategories",
  matH2: "Materials & finishes",
  matLabel: "Materials:",
  finLabel: "Finishes:",
  clustersH2: "Manufacturing clusters",
  clustersIntroPre: "Asia Sourcing operates from",
  clustersMoradabad: "Moradabad",
  clustersIntroPost: "with technical teams covering every relevant {group} cluster:",
  qcH2: "Product development, QC & export documentation",
  qcIntro: "Every programme runs the Asia Sourcing full stack:",
  qcBuyingHouse: "Buying house operations",
  qcBuyingHouseTail: "for retailers running the full programme end-to-end",
  qcStrategic: "Strategic sourcing",
  qcStrategicTail: "for multi-season category programmes",
  marketsH2: "International buyer notes",
  markets: [
    { label: "USA.", note: MARKET_NOTES.us, prefix: "See", links: [{ text: "our US buyer hub", to: "/usa" }] },
    { label: "UK.", note: MARKET_NOTES.uk, prefix: "See", links: [{ text: "our UK hub", to: "/regions/uk" }] },
    { label: "Europe.", note: MARKET_NOTES.eu, prefix: "See regional pages for", links: [
      { text: "Germany", to: "/regions/germany" },
      { text: "France", to: "/regions/france" },
      { text: "Netherlands", to: "/regions/netherlands" },
      { text: "Italy", to: "/regions/italy" },
      { text: "Spain", to: "/regions/spain" },
    ] },
    { label: "Australia & NZ.", note: MARKET_NOTES.au, prefix: "See", links: [
      { text: "AU", to: "/regions/australia" },
      { text: "NZ", to: "/regions/new-zealand" },
    ], joiner: " and " },
    { label: "South Africa.", note: MARKET_NOTES.za, prefix: "See", links: [{ text: "our South Africa hub", to: "/regions/south-africa" }] },
  ],
  faqH2: "Frequently asked",
  ctaH2: "Start a programme",
  ctaBody: "Share your category brief — SKU count, target FOB, destination market and launch window. We respond within one business day with a cluster recommendation, shortlist and timeline.",
  ctaButton: "Start a project →",
};

// Translated service-link labels (shared QC section). Missing ids fall back
// to the English label from the page data.
const SERVICE_LABELS = {
  de: {
    "product-development-india": "Produktentwicklung & Musterbau",
    "quality-control-india": "AQL 2.5 / 4.0 Inspektion",
    "export-documentation-india": "Exportdokumentation",
    "buying-house-india": "Einkaufsbüro-Betrieb",
    "strategic-sourcing-india": "Strategische Beschaffung",
  },
  fr: {
    "product-development-india": "Développement produit & échantillonnage",
    "quality-control-india": "Inspection AQL 2.5 / 4.0",
    "export-documentation-india": "Documentation export",
    "buying-house-india": "Opérations de buying house",
    "strategic-sourcing-india": "Sourcing stratégique",
  },
  es: {
    "product-development-india": "Desarrollo de producto y muestreo",
    "quality-control-india": "Inspección AQL 2.5 / 4.0",
    "export-documentation-india": "Documentación de exportación",
    "buying-house-india": "Operaciones de buying house",
    "strategic-sourcing-india": "Sourcing estratégico",
  },
};

const CategoryBody = ({ enPage, translation, locale }) => {
  const { open } = useStartProject();
  const page = translation ? { ...enPage, ...translation } : enPage;
  const s = translation ? translation.shared : EN_SHARED;
  const groupLabel = translation
    ? (s.groups[page.group] || page.group)
    : HOME_DECOR_GROUPS.find((g) => g.id === page.group)?.label || page.group;
  const serviceLabels = SERVICE_LABELS[locale] || {};

  const prefix = SUPPORTED_LOCALES.find((l) => l.code === locale)?.pathPrefix || "";
  const enPath = `/categories/home-decor/${enPage.slug}`;
  const isUntranslatedLocale = locale !== DEFAULT_LOCALE && !translation;
  const path = isUntranslatedLocale || locale === DEFAULT_LOCALE ? enPath : `${prefix}${enPath}`;
  const canonical = `${SITE_URL}${path}`;
  const L = (to) => (isUntranslatedLocale ? to : withLocale(to, locale));
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: page.h1,
      description: page.metaDescription,
      url: canonical,
      inLanguage: locale,
      about: page.primaryKeyword,
      provider: { "@type": "Organization", name: "Asia Sourcing", url: SITE_URL },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: s.breadcrumbHome, item: `${SITE_URL}${prefix}` },
        { "@type": "ListItem", position: 2, name: s.breadcrumbDecor, item: `${SITE_URL}${prefix}/categories/home-decor` },
        { "@type": "ListItem", position: 3, name: page.h1, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ];

  useSeo({
    title: page.title,
    description: page.metaDescription,
    path,
    canonical,
    alternates: [
      { hreflang: "en", href: `${SITE_URL}${enPath}` },
      ...HOME_DECOR_WAVE1_LOCALES.map((lc) => ({ hreflang: lc, href: `${SITE_URL}/${lc}${enPath}` })),
      { hreflang: "x-default", href: `${SITE_URL}${enPath}` },
    ],
    type: "website",
    jsonLd,
    jsonLdId: `homedecor-${enPage.slug}`,
  });

  const sections = page.sections || page.uniqueSections;

  return (
    <main className="qh-page" data-testid={`home-decor-${page.slug}`}>
      <nav className="qh-breadcrumb" aria-label="Breadcrumb" style={{ padding: "1rem 0", fontSize: ".78rem" }}>
        <Link to={L("/")}>{s.breadcrumbHome}</Link> · <Link to={L("/categories/home-decor")}>{s.breadcrumbDecor}</Link> · <span>{page.h1}</span>
      </nav>

      <header className="qh-hero" style={{ padding: "2.5rem 0 2rem" }}>
        <div className="qh-eyebrow">{page.eyebrow}</div>
        <h1 style={{ marginTop: ".5rem" }}>{page.h1}</h1>
        <p className="lede" style={{ marginTop: "1rem", maxWidth: "70ch" }}>{page.lede}</p>
      </header>

      <section>
        <h2>{s.subH2}</h2>
        <ul>
          {page.subcategories.map((sc, i) => <li key={i}>{sc}</li>)}
        </ul>
      </section>

      <section>
        <h2>{s.matH2}</h2>
        <p><strong>{s.matLabel}</strong> {page.materials.join(", ")}.</p>
        <p><strong>{s.finLabel}</strong> {page.finishes.join(", ")}.</p>
      </section>

      {sections.map((sec, i) => (
        <section key={i}>
          <h2>{sec.h2}</h2>
          {sec.body.map((p, j) => <p key={j}>{p}</p>)}
        </section>
      ))}

      <section>
        <h2>{s.clustersH2}</h2>
        <p>
          {s.clustersIntroPre}{" "}
          <Link to={L("/clusters/moradabad")}>{s.clustersMoradabad}</Link>{" "}
          {s.clustersIntroPost.replace("{group}", groupLabel.toLowerCase())}
        </p>
        <ul>
          {page.clusters.map((c, i) => (
            <li key={i}><Link to={L(`/clusters/${c.id}`)}>{c.id.charAt(0).toUpperCase() + c.id.slice(1)}</Link> — {c.note}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2>{s.qcH2}</h2>
        <p>{s.qcIntro}</p>
        <ul>
          {page.services.map((sv, i) => (
            <li key={i}><Link to={L(`/services/${sv.id}`)}>{serviceLabels[sv.id] || sv.label}</Link></li>
          ))}
          <li><Link to={L("/services/buying-house-india")}>{serviceLabels["buying-house-india"] || s.qcBuyingHouse}</Link> {s.qcBuyingHouseTail}</li>
          <li><Link to={L("/services/strategic-sourcing-india")}>{serviceLabels["strategic-sourcing-india"] || s.qcStrategic}</Link> {s.qcStrategicTail}</li>
        </ul>
      </section>

      <section>
        <h2>{s.marketsH2}</h2>
        {s.markets.map((m, i) => (
          <p key={i}>
            <strong>{m.label}</strong> {m.note} {m.prefix}{" "}
            {m.links.map((lk, j) => (
              <span key={j}>
                {j > 0 && (j === m.links.length - 1 && m.joiner ? m.joiner : ", ")}
                <Link to={lk.to}>{lk.text}</Link>
              </span>
            ))}
            .
          </p>
        ))}
      </section>

      <section>
        <h2>{s.faqH2}</h2>
        {page.faqs.map(([q, a], i) => (
          <details key={i} style={{ padding: ".9rem 0", borderBottom: "1px solid var(--line, #d4cfc3)" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500 }}>{q}</summary>
            <p style={{ marginTop: ".6rem" }}>{a}</p>
          </details>
        ))}
      </section>

      <section style={{ padding: "2.5rem 0" }}>
        <h2>{s.ctaH2}</h2>
        <p>{s.ctaBody}</p>
        <button
          type="button"
          className="qh-cta"
          onClick={open}
          data-testid={`home-decor-${page.slug}-cta`}
          style={{ marginTop: "1rem" }}
        >
          {s.ctaButton}
        </button>
      </section>
    </main>
  );
};

export const HomeDecorCategory = () => {
  const location = useLocation();
  const locale = detectLocale(location.pathname);
  const canonicalPath = stripLocale(location.pathname);
  const slug = canonicalPath.replace(/^\/categories\/home-decor\//, "").replace(/\/$/, "");
  const enPage = getHomeDecorCategory(slug);
  if (!enPage) return <NotFound />;
  const translation = locale !== DEFAULT_LOCALE ? getHomeDecorTranslation(locale, slug) : null;
  return <CategoryBody enPage={enPage} translation={translation} locale={locale} />;
};

export default HomeDecorCategory;
